import { defineIntegration } from '@teemill/modules/integration-framework';
import type { Component } from 'vue';

import {
  faBook,
  faCircleQuestion,
  faCog,
  faCommentSmile,
  faHome,
  faPeopleGroup,
  faQuestion,
  faSitemap,
  faUsers,
} from '@fortawesome/pro-light-svg-icons';

import dashboardRoute from './routes/dashboard';
import type { BrandContext } from './types/brandContext';
import type { BrandDetails } from './types/brandDetails';
import type { LanguageAndCopyRules } from './types/languageAndCopyRules';
import type { ProductGraphicDesignAesthetic } from './types/productGraphicDesignAesthetic';
import type { VisualArtDirection } from './types/visualArtDirection';

export type CoreConfig = {
  force_2FA: boolean;
  dashboard: {
    actions: { href: string; text: string }[];
  };
  header: {
    actions: {
      name: string;
      component: Component;
    }[];
  };
  users: {
    edit: {
      formExtraSections: {
        name: string;
        component: Component<{
          formData: any;
        }>;
      }[];
      onMounted: ((formData: Record<string, any>, ...optionalParams: any[]) => Promise<any>)[];
    };
  };
  settings: {
    additionalData: {
      onMounted: ((formData: Record<string, any>, ...optionalParams: any[]) => Promise<any>)[];
    };
  };
  tone_of_voice?: string;
  summary?: string;
  details?: BrandDetails;
  brand_context?: BrandContext;
  visual_art_direction?: VisualArtDirection;
  product_graphic_design_aesthetic?: ProductGraphicDesignAesthetic;
  language_and_copy_rules?: LanguageAndCopyRules | null;
  brand_context_reference_file_refs?: { driveSrc: string; src?: string; type: 'image' | 'pdf' }[];
  brand_context_reference_files?: BrandContextReferenceFiles;
  mainsite_url?: string | null;
};

export type ReferenceFile = {
  src?: string;
  driveSrc?: string;
  type?: 'image' | 'pdf';
};

export type BrandContextReferenceFiles = {
  brand_voice?: ReferenceFile[];
  photography?: ReferenceFile[];
  design?: ReferenceFile[];
};

export default defineIntegration('core', {
  config: (): CoreConfig => ({
    force_2FA: false,
    dashboard: {
      actions: [],
    },
    header: {
      actions: [],
    },
    users: {
      edit: {
        formExtraSections: [],
        onMounted: [],
      },
    },
    settings: {
      additionalData: {
        onMounted: [],
      },
    },
  }),

  async setup({ menu, route }) {
    route(dashboardRoute);

    // redirect from the old tutorials page to the new one
    route({
      path: '/factory/tutorials/:blogSlug',
      name: 'factory.tutorials.view',
      redirect: ({ params }) => ({
        name: 'core.support.tutorials.view',
        params: { blogSlug: params.blogSlug },
      }),
    });

    menu({
      name: 'core.dashboard',
      title: 'Dashboard',
      to: { name: 'project.dashboard' },
      order: -1, //negative to make sure it's the first item on the menu
      icon: faHome,
    });

    menu({
      name: 'core.chat',
      title: 'Chat',
      to: { name: 'chat.index' },
      order: 311,
      icon: faCommentSmile,
    });

    menu({
      name: 'users',
      title: 'Users',
      to: { name: 'project.users.list' },
      order: 500,
      icon: faUsers,
    });

    menu({
      name: 'users.roles',
      title: 'Roles',
      to: { name: 'project.users.roles.list' },
      order: 1,
      icon: faSitemap,
    });
    menu({
      name: 'users.groups',
      title: 'Groups',
      to: { name: 'project.users.groups.list' },
      order: 2,
      icon: faPeopleGroup,
    });

    menu({
      name: 'support',
      title: 'Support',
      to: { name: 'core.support' },
      order: 600,
      icon: faCircleQuestion,
    });

    menu({
      name: 'support.chat',
      title: 'Chat',
      to: { name: 'chat.dashboard' },
      order: 1,
      icon: faCommentSmile,
    });

    menu({
      name: 'support.tutorials',
      title: 'Tutorials',
      to: { name: 'core.support.tutorials.list' },
      order: 2,
      icon: faBook,
    });

    menu({
      name: 'support.faqs',
      title: 'FAQs',
      to: { name: 'core.support.faqs' },
      order: 3,
      icon: faQuestion,
    });

    menu({
      name: 'core.settings',
      title: 'Settings',
      to: { name: 'projects.settings' },
      order: 1000000,
      icon: faCog,
    });
  },
});
